import {
  type ApprovalList,
  ApprovalListSchema,
  ApprovalResponseSchema,
  ApproveResponseSchema,
  BusinessResponseSchema,
  type BusinessRole,
  InvitePreviewSchema,
  InviteResponseSchema,
  MemberResponseSchema,
  PeopleSchema,
} from '@oneapp/shared';
import { z } from 'zod';
import { api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
const id = (value: string) => encodeURIComponent(value);

/** People in a business and invites (Phase 14; see packages/shared/src/api.ts). */
export const peopleApi = {
  list: (businessId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/businesses/${id(businessId)}/members`, PeopleSchema, { auth: true, ...withSignal(signal) }),

  /** A fresh Idempotency-Key per tap: the link comes back once, with the invite. */
  invite: (businessId: string, input: { role: BusinessRole; phone: string }, idempotencyKey: string) =>
    api.post(`/api/v1/businesses/${id(businessId)}/invites`, InviteResponseSchema, {
      auth: true,
      body: input,
      idempotencyKey,
    }),

  cancelInvite: (businessId: string, inviteId: string) =>
    api.delete(`/api/v1/businesses/${id(businessId)}/invites/${id(inviteId)}`, z.null(), { auth: true }),

  setRole: (businessId: string, userId: string, role: BusinessRole) =>
    api.patch(`/api/v1/businesses/${id(businessId)}/members/${id(userId)}`, MemberResponseSchema, {
      auth: true,
      body: { role },
    }),

  remove: (businessId: string, userId: string) =>
    api.delete(`/api/v1/businesses/${id(businessId)}/members/${id(userId)}`, z.null(), { auth: true }),

  preview: (token: string, signal?: AbortSignal) =>
    api.get(`/api/v1/invites/${id(token)}`, InvitePreviewSchema, { auth: true, ...withSignal(signal) }),

  accept: (token: string, idempotencyKey: string) =>
    api.post(`/api/v1/invites/${id(token)}/accept`, BusinessResponseSchema, { auth: true, idempotencyKey }),
};

/** Approval requests (Phase 14). */
export const approvalsApi = {
  list: (status: 'pending' | 'done' | null, signal?: AbortSignal): Promise<ApprovalList> =>
    api.get(`/api/v1/approval-requests${status ? `?status=${status}` : ''}`, ApprovalListSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  get: (requestId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/approval-requests/${id(requestId)}`, ApprovalResponseSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  approve: (requestId: string, idempotencyKey: string) =>
    api.post(`/api/v1/approval-requests/${id(requestId)}/approve`, ApproveResponseSchema, {
      auth: true,
      idempotencyKey,
    }),

  sendBack: (requestId: string, comment: string, idempotencyKey: string) =>
    api.post(`/api/v1/approval-requests/${id(requestId)}/send-back`, ApprovalResponseSchema, {
      auth: true,
      body: { comment },
      idempotencyKey,
    }),

  remind: (requestId: string) =>
    api.post(`/api/v1/approval-requests/${id(requestId)}/remind`, ApprovalResponseSchema, { auth: true }),
};

/** Query keys, so every screen showing people or approvals refreshes together. */
export const peopleKeys = {
  one: (businessId: string) => ['businesses', businessId, 'people'] as const,
  invite: (token: string) => ['invites', token] as const,
};
export const approvalKeys = {
  all: ['approvals'] as const,
  list: (status: 'pending' | 'done' | null) => ['approvals', 'list', status ?? 'all'] as const,
  one: (requestId: string) => ['approvals', requestId] as const,
};
